/*
 * Project: Goalscape
 * File:    AddBookmarkModal.js
 * Path:    src/components/bookmark/AddBookmarkModal.js
 * Description: Modal component for adding a new bookmark.
 * Author:  Akmal Djumakhodjaev
 * Created: [Creation Date - YYYY-MM-DD]
 * Modified: 2025-03-15
 * AI Hint: Implement URL validation and sanitization to prevent security vulnerabilities. Consider using a service like metascraper for more reliable metadata extraction.
 */
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchUrlMetadata } from '../../utils/bookmarkUtils';
import { useBookmarks } from '../../hooks/useBookmarks';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { BookmarkIcon } from '../icons/BookmarkIcon';
import {
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalTitle,
    CloseButton,
    Form,
    InputGroup,
    Label,
    Input,
    PreviewCard,
    PreviewImage,
    PreviewContent,
    PreviewTitle,
    PreviewUrl,
    ButtonGroup,
    CancelButton,
    SaveButton,
    LoadingSpinner,
    ErrorMessage
} from '../../styles/AddBookmarkModal.styles';

const AddBookmarkModalContent = ({ onClose, userId }) => {
    useBodyScrollLock();
    const { addBookmark } = useBookmarks(userId);
    const [url, setUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [preview, setPreview] = useState(null);
    const [editedTitle, setEditedTitle] = useState('');

    const resetState = () => {
        setUrl('');
        setError('');
        setPreview(null);
        setLoading(false);
        setEditedTitle('');
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const validateUrl = (url) => {
        try {
            new URL(url);
            return true;
        } catch {
            return false;
        }
    };

    const handleUrlChange = async (e) => {
        const newUrl = e.target.value;
        setUrl(newUrl);
        setError('');

        if (!newUrl) {
            setPreview(null);
            return;
        }

        if (!validateUrl(newUrl)) {
            setError('Please enter a valid URL');
            setPreview(null);
            return;
        }

        try {
            setLoading(true);
            const metadata = await fetchUrlMetadata(newUrl);
            setPreview({
                url: newUrl,
                title: metadata.title,
                previewImage: metadata.previewImage
            });
            setEditedTitle(metadata.title || '');
            setError('');
        } catch (err) {
            if (err.message === 'Title not found in webhook response') {
                setError('Could not extract title from URL. Please enter a title manually.');
            } else {
                setError('Error fetching URL preview');
            }
            setPreview(null);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
    
        if (!url) {
            setError('Please enter a URL');
            return;
        }
    
        if (!validateUrl(url)) {
            setError('Please enter a valid URL');
            return;
        }
    
        try {
            setLoading(true);
            await addBookmark(url, editedTitle);
            handleClose();
        } catch (err) {
            setError('Error adding bookmark');
        } finally {
            setLoading(false);
        }
    };

    const modalContent = (
        <ModalOverlay>
            <ModalContent>
                <ModalHeader>
                    <ModalTitle>Add Bookmark</ModalTitle>
                    <CloseButton onClick={handleClose}>&times;</CloseButton>
                </ModalHeader>
                <Form onSubmit={handleSubmit}>
                    <InputGroup>
                        <Label htmlFor="bookmark-url">Bookmark URL</Label>
                        <Input
                            id="bookmark-url"
                            type="text"
                            value={url}
                            onChange={handleUrlChange}
                            placeholder="https://example.com"
                            disabled={loading}
                            autoFocus
                        />
                        {error && <ErrorMessage>{error}</ErrorMessage>}
                    </InputGroup>

                    {preview && (
                        <>
                            <InputGroup>
                                <Label htmlFor="bookmark-title">Title</Label>
                                <Input
                                    id="bookmark-title"
                                    type="text"
                                    value={editedTitle}
                                    onChange={(e) => setEditedTitle(e.target.value)}
                                    placeholder="Enter title"
                                    disabled={loading}
                                />
                            </InputGroup>
                            <PreviewCard>
                                {preview.previewImage ? (
                                    <PreviewImage
                                        src={preview.previewImage}
                                        alt={preview.title}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.nextElementSibling.style.display = 'flex';
                                        }}
                                    />
                                ) : (
                                    <div className="fallback-preview">
                                        <BookmarkIcon />
                                    </div>
                                )}
                                <PreviewContent>
                                    <PreviewTitle>
                                        {editedTitle || 'Untitled Bookmark'}
                                    </PreviewTitle>
                                    <PreviewUrl>{url}</PreviewUrl>
                                </PreviewContent>
                            </PreviewCard>
                        </>
                    )}

                    <ButtonGroup>
                        <CancelButton type="button" onClick={handleClose} disabled={loading}>
                            Cancel
                        </CancelButton>
                        <SaveButton type="submit" disabled={loading || !!error}>
                            {loading ? <LoadingSpinner /> : 'Add Bookmark'}
                        </SaveButton>
                    </ButtonGroup>
                </Form>
            </ModalContent>
        </ModalOverlay>
    );

    return createPortal(modalContent, document.body);
};


const AddBookmarkModal = ({ isOpen, onClose, userId }) => {
    if (!isOpen) {
        return null;
    }

    return <AddBookmarkModalContent onClose={onClose} userId={userId} />;
};

export default AddBookmarkModal;